/**
 * The Field — main scene composition.
 *
 * This is where every system comes together:
 * renderer, camera, nodes, edges, entry ritual.
 *
 * The field is not a dashboard.
 * It is a spatial environment you inhabit.
 */

import * as THREE from 'three';
import { FieldCamera } from './camera';
import { NodeRenderer } from './nodes';
import { EdgeRenderer } from './edges';
import { EntryRitual } from './entry';
import { HoverSystem } from '../interaction/hover';
import { SelectionSystem } from '../interaction/selection';
import { ModeSystem } from '../interaction/modes';
import { QuerySystem } from '../interaction/query';
import { ValidateInteraction } from '../interaction/validate-interaction';
import { LearnInteraction } from '../interaction/learn-interaction';
import { FieldInteraction } from '../interaction/field-interaction';
import { SenseMode } from '../interaction/sense-mode';
import { Onboarding } from '../interaction/onboarding';
import { SettingsInteraction } from '../interaction/settings-interaction';
import { FieldLabels } from './labels';
import { FieldAnnotations } from './annotations';
import { FieldTendrils } from './tendrils';
import { ValidateOverlay } from './validate-overlay';
import { ZoneNavigation } from './zone';
import { generateSyntheticField, packFieldData, type SyntheticField } from '../data/synthetic';
import { transformLiveData } from '../data/live';
import { getFieldData, useLiveData } from '../api/client';
import { useAuraStore } from '../state/store';

export class Field {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private fieldCamera: FieldCamera;
  private nodeRenderer: NodeRenderer;
  private edgeRenderer: EdgeRenderer;
  private entryRitual: EntryRitual;
  private hoverSystem: HoverSystem;
  private selectionSystem: SelectionSystem;
  private modeSystem: ModeSystem;
  private fieldLabels: FieldLabels;
  private fieldTendrils: FieldTendrils;
  private validateOverlay: ValidateOverlay;
  private zoneNavigation: ZoneNavigation;
  private querySystem: QuerySystem;
  private validateInteraction: ValidateInteraction;
  private learnInteraction: LearnInteraction;
  private fieldInteraction: FieldInteraction;
  private senseMode: SenseMode;
  private onboarding: Onboarding;
  private annotations: FieldAnnotations;
  private settingsInteraction: SettingsInteraction;

  private field: SyntheticField;
  private clock = new THREE.Clock();
  private animationId: number = 0;

  private liveDataLoading = false;

  constructor(private container: HTMLElement) {
    // Start with synthetic data immediately (fast init)
    this.field = generateSyntheticField(42);
    const buffers = packFieldData(this.field);

    console.log(
      `Field initialized: ${this.field.nodes.length} nodes, ${this.field.edges.length} edges`,
    );

    // If live data is configured, fetch it — entry ritual is deferred
    // until real data arrives (prevents synthetic data flash)
    const waitingForLive = useLiveData();
    if (waitingForLive) {
      this.loadLiveData();
    }

    // WebGL renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x06060d, 1); // Near-black with hint of warmth
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    container.appendChild(this.renderer.domElement);

    // Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x06060d, 0.00025);

    // Camera
    this.fieldCamera = new FieldCamera(container);

    // Node renderer (the star of the show)
    this.nodeRenderer = new NodeRenderer(buffers);
    this.scene.add(this.nodeRenderer.mesh);

    // Edge renderer
    this.edgeRenderer = new EdgeRenderer(this.field.nodes, this.field.edges);
    this.scene.add(this.edgeRenderer.lines);

    // Structural tendrils — organic curves connecting clusters
    this.fieldTendrils = new FieldTendrils(this.field);
    this.scene.add(this.fieldTendrils.group);

    // Validate overlay — diagrammatic boundary rings (invisible until Validate mode)
    this.validateOverlay = new ValidateOverlay(this.field);
    this.scene.add(this.validateOverlay.group);

    // Spatial typography — kept for zone navigation but suppressed visually.
    // The 2D FieldAnnotations system renders Tufte-style leader lines instead.
    this.fieldLabels = new FieldLabels(this.field);
    this.fieldLabels.group.visible = false; // annotations.ts renders labels
    this.scene.add(this.fieldLabels.group);

    // Ambient dust — very subtle particles for depth perception
    this.addAmbientDust();

    // Entry ritual — deferred when waiting for live data (screen stays black)
    this.entryRitual = new EntryRitual(
      waitingForLive ? { deferAutoSkip: true } : undefined,
    );

    // Zone/depth navigation (before interaction systems — intercepts category clicks)
    this.zoneNavigation = new ZoneNavigation(
      container,
      this.fieldCamera.camera,
      this.fieldCamera,
      this.fieldLabels,
      this.field,
    );

    // Interaction systems
    this.hoverSystem = new HoverSystem(
      container,
      this.fieldCamera.camera,
      this.nodeRenderer,
      this.edgeRenderer,
      this.field,
    );
    this.selectionSystem = new SelectionSystem(
      container,
      this.fieldCamera.camera,
      this.fieldCamera,
      this.nodeRenderer,
      this.field,
      this.zoneNavigation,
    );
    this.modeSystem = new ModeSystem();

    // Query system — ask the field, it answers spatially
    this.querySystem = new QuerySystem(
      this.field,
      this.fieldCamera,
      this.nodeRenderer,
      this.zoneNavigation,
    );

    // Validation interaction — epistemology panel in Validate mode
    this.validateInteraction = new ValidateInteraction(
      container,
      this.fieldCamera.camera,
      this.fieldCamera,
      this.nodeRenderer,
      this.field,
    );

    // Learn interaction — comparative synthesis panel in Learn mode
    this.learnInteraction = new LearnInteraction(
      container,
      this.fieldCamera.camera,
      this.fieldCamera,
      this.nodeRenderer,
      this.field,
      this.zoneNavigation,
    );

    // Field interaction — unified activation mode (replaces Create/Activate/Evolve)
    this.fieldInteraction = new FieldInteraction(this.field);

    // Sense mode — full-screen intelligence feed
    this.senseMode = new SenseMode();

    // Context layer — always-on explanations
    this.onboarding = new Onboarding();

    // Tufte-style leader-line annotations
    this.annotations = new FieldAnnotations(container, this.field, this.fieldCamera.camera);

    // Settings / Control Panel
    this.settingsInteraction = new SettingsInteraction();

    // Resize handler
    const resizeObserver = new ResizeObserver(() => this.onResize());
    resizeObserver.observe(container);

    // Start animation loop — entry ritual only begins when data is ready
    if (!waitingForLive) {
      this.entryRitual.start();
    }
    // else: entry stays black until loadLiveData() calls release()
    this.animate();
  }

  /**
   * Fetch live data from the AURA gateway and hot-swap into the field.
   * Replaces synthetic nodes/edges with real SERP-sourced signals.
   */
  private async loadLiveData() {
    this.liveDataLoading = true;
    console.log('[Field] Fetching live data from AURA gateway…');

    try {
      const apiData = await getFieldData();
      if (!apiData) {
        console.warn('[Field] No data from gateway — staying on synthetic');
        this.liveDataLoading = false;
        return;
      }
      const liveField = transformLiveData(apiData);

      // Hot-swap the field data
      this.field = liveField;
      const buffers = packFieldData(liveField);

      // Rebuild ALL renderers with live data
      this.scene.remove(this.nodeRenderer.mesh);
      this.nodeRenderer.dispose();
      this.nodeRenderer = new NodeRenderer(buffers);
      this.scene.add(this.nodeRenderer.mesh);

      this.scene.remove(this.edgeRenderer.lines);
      this.edgeRenderer.dispose();
      this.edgeRenderer = new EdgeRenderer(liveField.nodes, liveField.edges);
      this.scene.add(this.edgeRenderer.lines);

      this.scene.remove(this.fieldTendrils.group);
      this.fieldTendrils.dispose();
      this.fieldTendrils = new FieldTendrils(liveField);
      this.scene.add(this.fieldTendrils.group);

      this.scene.remove(this.validateOverlay.group);
      this.validateOverlay.dispose();
      this.validateOverlay = new ValidateOverlay(liveField);
      this.scene.add(this.validateOverlay.group);

      this.scene.remove(this.fieldLabels.group);
      this.fieldLabels.dispose();
      this.fieldLabels = new FieldLabels(liveField);
      this.scene.add(this.fieldLabels.group);

      // Rebuild ALL navigation and interaction systems with live data
      this.zoneNavigation.dispose();
      this.zoneNavigation = new ZoneNavigation(
        this.container,
        this.fieldCamera.camera,
        this.fieldCamera,
        this.fieldLabels,
        liveField,
      );

      this.hoverSystem.dispose();
      this.hoverSystem = new HoverSystem(
        this.container,
        this.fieldCamera.camera,
        this.nodeRenderer,
        this.edgeRenderer,
        liveField,
      );

      this.selectionSystem.dispose();
      this.selectionSystem = new SelectionSystem(
        this.container,
        this.fieldCamera.camera,
        this.fieldCamera,
        this.nodeRenderer,
        liveField,
        this.zoneNavigation,
      );

      this.querySystem.dispose();
      this.querySystem = new QuerySystem(
        liveField,
        this.fieldCamera,
        this.nodeRenderer,
        this.zoneNavigation,
      );

      this.validateInteraction.dispose();
      this.validateInteraction = new ValidateInteraction(
        this.container,
        this.fieldCamera.camera,
        this.fieldCamera,
        this.nodeRenderer,
        liveField,
      );

      this.learnInteraction.dispose();
      this.learnInteraction = new LearnInteraction(
        this.container,
        this.fieldCamera.camera,
        this.fieldCamera,
        this.nodeRenderer,
        liveField,
        this.zoneNavigation,
      );

      this.fieldInteraction.dispose();
      this.fieldInteraction = new FieldInteraction(liveField);
      this.annotations.setField(liveField);

      // Reset UI state — clear any stale context
      const store = useAuraStore.getState();
      store.setDepth('overview');
      store.setActiveZone(null);
      store.setActiveCluster(null);
      store.clearSelection();
      store.clearCameraStack();

      // Hide any open panels
      document.getElementById('territory-panel')?.classList.remove('visible');
      document.getElementById('cluster-detail')?.classList.remove('visible');
      document.getElementById('provenance-card')?.classList.remove('visible');

      // Now that real data is loaded, begin the entry ritual
      // (screen was held black while fetching — this starts the reveal)
      this.entryRitual.release();
      this.onboarding.onLiveDataLoaded(liveField.nodes.filter(n => !n.id.includes('-e')).length);

      console.log(
        `[Field] Live data loaded: ${liveField.nodes.length} nodes, ${liveField.edges.length} edges — all systems rebuilt`,
      );
    } catch (err) {
      console.warn('[Field] Live data fetch failed, falling back to synthetic:', err);
      // Don't leave a black screen — start entry with synthetic data
      this.entryRitual.release();
    } finally {
      this.liveDataLoading = false;
    }
  }

  /**
   * Add ambient dust particles for depth perception.
   * Tiny, dim points scattered throughout the volume.
   */
  private addAmbientDust() {
    const count = 2000;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2000;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0x1a1a2e,
      size: 0.8,
      transparent: true,
      opacity: 0.12,
      depthWrite: false,
      blending: THREE.NormalBlending,
      sizeAttenuation: true,
    });

    const dust = new THREE.Points(geometry, material);
    this.scene.add(dust);
  }

  /**
   * Main animation loop.
   */
  private animate = () => {
    this.animationId = requestAnimationFrame(this.animate);

    const deltaTime = this.clock.getDelta();
    const elapsed = this.clock.getElapsedTime();

    // Entry ritual
    const entryProgress = this.entryRitual.update();

    // Camera
    this.fieldCamera.update(deltaTime);

    // Mode
    const modeIndex = this.modeSystem.update();

    // Node renderer
    this.nodeRenderer.update(elapsed, entryProgress, modeIndex);

    // Structural elements
    this.fieldTendrils.update(entryProgress);
    this.validateOverlay.update(entryProgress);
    this.fieldLabels.update(entryProgress, this.fieldCamera.camera.position);

    // Interaction (only after entry)
    this.hoverSystem.update();

    // Query system (smooth highlight transitions)
    this.querySystem.update();

    // Validation interaction (panel visibility in Validate mode)
    this.validateInteraction.update();

    // Learn interaction (panel visibility in Learn mode)
    this.learnInteraction.update();

    // Field interaction (unified activation mode)
    this.fieldInteraction.update(this.clock.getDelta());

    // Active field cluster highlight — pulse the live cluster
    const activeIdx = useAuraStore.getState().activeFieldClusterIndex;
    if (activeIdx !== null && activeIdx >= 0) {
      const scores = new Float32Array(this.field.nodes.length);
      // Full relevance for active cluster + its satellites, dim everything else
      for (let i = 0; i < this.field.nodes.length; i++) {
        // Nodes near the active cluster get highlighted (within the cluster's satellite group)
        const node = this.field.nodes[i];
        if (i === activeIdx) {
          scores[i] = 1.0;
        } else if (node.id && this.field.nodes[activeIdx]?.id && node.id.startsWith(this.field.nodes[activeIdx].id)) {
          scores[i] = 0.8; // satellites of active cluster
        } else {
          scores[i] = 0.15; // everything else dimmed
        }
      }
      this.nodeRenderer.setQueryRelevance(scores);
      this.nodeRenderer.material.uniforms.u_queryHighlight.value = 0.45;
    }

    // Sense mode intelligence feed
    this.senseMode.update();

    // Context layer update
    this.onboarding.update();

    // Render scene
    this.renderer.render(this.scene, this.fieldCamera.camera);

    // Tufte leader-line annotations (must run after render so projection is current)
    this.annotations.update(this.container.clientWidth, this.container.clientHeight);
  };

  private onResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.fieldCamera.camera.aspect = width / height;
    this.fieldCamera.camera.updateProjectionMatrix();
  }

  dispose() {
    cancelAnimationFrame(this.animationId);
    this.nodeRenderer.dispose();
    this.edgeRenderer.dispose();
    this.fieldLabels.dispose();
    this.fieldTendrils.dispose();
    this.validateOverlay.dispose();
    this.zoneNavigation.dispose();
    this.hoverSystem.dispose();
    this.selectionSystem.dispose();
    this.modeSystem.dispose();
    this.querySystem.dispose();
    this.validateInteraction.dispose();
    this.learnInteraction.dispose();
    this.fieldInteraction.dispose();
    this.senseMode.dispose();
    this.annotations.dispose();
    this.settingsInteraction.dispose();
    this.renderer.dispose();
  }
}
